<script setup lang="ts">
  import { ref } from 'vue';
  import { useRouter } from 'vue-router';
  import LogoutIcon from '@/shared/assets/icons/LogoutIcon.svg';
  import { IconImage, Tooltip, UserAvatar } from '@/shared/ui';
  import { useAuthStore } from '@/entities/Session';
  import { useCustomToast } from '@/shared/lib/use';
  import { formatShortName } from '@/shared/lib/name';

  interface Props {
    isCollapsed: boolean;
    userName: string;
    userEmail: string;
  }

  const props = defineProps<Props>();

  const isLoading = ref(false);
  const authStore = useAuthStore();
  const router = useRouter();
  const toast = useCustomToast();

  const handleLogout = async () => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
      await authStore.logout();

      toast.showSuccessToast({
        title: 'Sucesso',
        message: 'Usuário deslogado com sucesso!',
        type: 'success',
      });

      router.push('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
</script>

<template>
  <button
    data-testid="logout-btn"
    :class="[
      'bg-logout flex items-center bg-gray-04',
      props.isCollapsed
        ? 'w-10 h-10 rounded-full'
        : 'w-50 h-11 mr-1.5 py-2 px-3 rounded-lg justify-between',
    ]"
    :disabled="isLoading"
    @click="handleLogout"
  >
    <div
      :class="[
        'flex items-center',
        props.isCollapsed ? 'justify-center w-full' : 'justify-between w-full',
      ]"
    >
      <Tooltip
        v-if="props.isCollapsed"
        content="Sair"
        placement="right"
      >
        <UserAvatar
          v-if="props.isCollapsed"
          :name="props.userName"
        />
      </Tooltip>
      <div
        v-if="!props.isCollapsed"
        class="text-[var(--theme-text)] flex items-center justify-between ml-1 w-full"
      >
        <div class="flex items-center min-w-0 flex-1">
          <UserAvatar :name="props.userName" />
          <span class="m-2 text-[var(--theme-text)] text-xs truncate min-w-0">{{
            formatShortName(props.userName)
          }}</span>
        </div>
        <IconImage
          v-if="!props.isCollapsed"
          class="text-[var(--theme-secondary-ink)]"
          :src="LogoutIcon"
          alt="Logout"
        />
      </div>
    </div>
  </button>
</template>

<style scoped>
  .bg-logout {
    background-color: color-mix(in srgb, var(--theme-primary) 95%, var(--theme-secondary));
  }
</style>
